import { useState, useEffect, useMemo } from 'react';
import { Building2, RefreshCw, Search } from 'lucide-react';
import { getCompanies, updateCompanyTag } from '../api/api';
import { Toast } from '../components/ui';
import styles from './Companies.module.scss';

// Sentinel select value meaning "type a brand-new tag".
const NEW_TAG = '__new__';

function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Tag editing state (one row at a time)
  const [editingTagId, setEditingTagId] = useState(null);
  const [tagDraft, setTagDraft] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [savingTag, setSavingTag] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getCompanies();
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Error fetching companies');
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return companies;
    }
    return companies.filter(
      company =>
        (company.name || '').toLowerCase().includes(needle) ||
        (company.company_id || '').toLowerCase().includes(needle)
    );
  }, [companies, search]);

  const formatTime = value => (value ? new Date(value).toLocaleString() : '—');

  const existingTags = useMemo(
    () => [...new Set(companies.map(company => company.tag).filter(Boolean))].sort(),
    [companies]
  );

  const startTagEdit = company => {
    setEditingTagId(company.company_id);
    setTagDraft(company.tag || '');
    setNewTagValue('');
  };

  const saveTag = async company => {
    const isNew = tagDraft === NEW_TAG;
    const tag = isNew ? newTagValue.trim() : tagDraft;
    if (isNew) {
      if (!tag) {
        return;
      }
    }

    try {
      setSavingTag(true);
      await updateCompanyTag(company.company_id, tag || null);
      setEditingTagId(null);
      await fetchCompanies();
    } catch (err) {
      console.error('Error updating company tag:', err);
      setError('Error updating company tag');
    } finally {
      setSavingTag(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div className={styles.headerIdentity}>
          <span className={styles.headerIcon}>
            <Building2 size={22} />
          </span>
          <div>
            <p>User Cloud directory</p>
            <h1>Companies</h1>
            <span>Review synchronized organizations and organize them with local tags.</span>
          </div>
        </div>
        <button onClick={fetchCompanies} className={styles.refreshButton} disabled={loading}>
          <RefreshCw size={16} className={loading ? styles.spinning : ''} />
          {loading ? 'Refreshing' : 'Refresh directory'}
        </button>
      </header>

      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}

      <section className={styles.directoryPanel}>
        <div className={styles.toolBar}>
          <Search size={16} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by company name or ID"
            className={styles.searchInput}
          />
        </div>

        <div className={styles.resultCount}>
          <span>
            Showing <strong>{filteredCompanies.length}</strong> of {companies.length} companies
          </span>
          <span>Company data is maintained by Cloud Sync</span>
        </div>

        {loading && companies.length === 0 ? (
          <div className={styles.loadingMessage}>Loading companies...</div>
        ) : filteredCompanies.length === 0 ? (
          <div className={styles.emptyMessage}>No companies found.</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.companiesTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company ID</th>
                  <th>Tag</th>
                  <th>Partner Company</th>
                  <th>Machines</th>
                  <th>Last Synced Update</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map(company => (
                  <tr key={company.company_id}>
                    <td>{company.name}</td>
                    <td className={styles.idCell}>{company.company_id}</td>
                    <td className={styles.tagCell}>
                      {editingTagId === company.company_id ? (
                        <div className={styles.tagEditor}>
                          <select
                            value={tagDraft}
                            onChange={e => setTagDraft(e.target.value)}
                            className={styles.tagSelect}
                            disabled={savingTag}
                          >
                            <option value="">(no tag)</option>
                            {existingTags.map(tag => (
                              <option key={tag} value={tag}>
                                {tag}
                              </option>
                            ))}
                            <option value={NEW_TAG}>+ New tag...</option>
                          </select>
                          {tagDraft === NEW_TAG && (
                            <input
                              type="text"
                              value={newTagValue}
                              onChange={e => setNewTagValue(e.target.value)}
                              placeholder="New tag name"
                              className={styles.tagInput}
                              disabled={savingTag}
                            />
                          )}
                          <button
                            onClick={() => saveTag(company)}
                            className={styles.tagSaveButton}
                            disabled={savingTag}
                          >
                            {savingTag ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingTagId(null)}
                            className={styles.tagCancelButton}
                            disabled={savingTag}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className={styles.tagDisplay}>
                          {company.tag ? (
                            <span className={styles.tagBadge}>{company.tag}</span>
                          ) : (
                            <span className={styles.noTag}>—</span>
                          )}
                          <button
                            onClick={() => startTagEdit(company)}
                            className={styles.tagEditButton}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </td>
                    <td className={styles.partnerCell}>
                      {company.is_partner ? (
                        <span className={styles.partnerSelfBadge}>Partner</span>
                      ) : company.partner_company_name ? (
                        company.partner_company_name
                      ) : (
                        <span className={styles.noTag}>—</span>
                      )}
                    </td>
                    <td>{company.machine_count}</td>
                    <td className={styles.timeCell}>{formatTime(company.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default Companies;
